import * as React from 'react';

import '@ant-design/compatible/assets/index.css';
import { Modal, Form, Alert, notification, Select } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { useState } from 'react';
import {
	copyRankForSuppliersToLocationsForBuyer,
	copyRankForSuppliersToLocationsForSupplier,
} from '../../thunks/supplier_private_networks_thunks';
import RegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import ProblemTypeHierarchicalDisplay from '../problem_type_hierarchical_display/ProblemTypeHierarchicalDisplay';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { withRouter } from 'react-router';
import { ROLE_TYPES } from '../../utils/DataConstants';

const FormItem = Form.Item;

function CopyRankingToLocationsComponent(props) {
	const [form] = Form.useForm();
	const {
		selectedLocation,
		locations,
		visible,
		onCancel,
		copyRankings,
		onSuccess,
		problemTypeIds = [],
	} = props;

	// Respect any active problem type filter: copy only the filtered problem types
	// instead of every one. An empty list tells the backend to copy every problem type.
	const hasProblemTypeFilter = problemTypeIds.length > 0;

	const [confirmLoading, setConfirmLoading] = useState(false);

	const onSubmit = (values) => {
		setConfirmLoading(true);
		const toLocationIds = values.locationIds || [];
		const problemTypeIdsToCopy = hasProblemTypeFilter ? problemTypeIds : undefined;
		copyRankings({
			fromLocationId: selectedLocation.id,
			toLocationIds,
			problemTypeIds: problemTypeIdsToCopy,
		})
			.then((result) => {
				const failureCount = nullSafeGetOrElse('failureCount', result, 0);
				if (failureCount > 0) {
					notification.warning({
						message: 'Some locations could not be updated',
						description: `Supplier assignments were copied to ${nullSafeGetOrElse(
							'successCount',
							result,
							0
						)} of ${toLocationIds.length} locations. ${failureCount} failed.`,
					});
				} else {
					notification.success({
						message: 'Supplier assignments copied',
						description: `Copied to ${toLocationIds.length} location${
							toLocationIds.length === 1 ? '' : 's'
						}.`,
					});
				}
				// Clear the selected locations so reopening the modal starts fresh
				// rather than showing the locations from the previous copy.
				form.resetFields();
				onSuccess();
			})
			.catch((errorMessage) => {
				notification.error({
					message: 'Unable to copy supplier assignments',
					description: errorMessage || 'Something went wrong. Please try again.',
				});
			})
			.finally(() => {
				setConfirmLoading(false);
			});
	};

	if (!visible) return null;
	return (
		<Modal
			visible={visible}
			width={700}
			okText="Next"
			onCancel={onCancel}
			onOk={() => {
				form.validateFields(['locationIds']).then(() => {
					const toLocationIds = form.getFieldValue('locationIds') || [];
					Modal.confirm({
						width: 520,
						title: (
							<span className="flex items-start">
								<span className="max-w-[70%] break-words" title={selectedLocation.name}>
									{selectedLocation.name}
								</span>
								<ArrowRightOutlined translate="" className="mx-2 mt-0.5 text-gray-400" />
								<span className="whitespace-nowrap">
									{toLocationIds.length} location{toLocationIds.length === 1 ? '' : 's'}
								</span>
							</span>
						),
						content: (
							<div>
								This will overwrite supplier assignments at the selected locations for{' '}
								{hasProblemTypeFilter ? 'the selected' : 'all'} problem types.
							</div>
						),
						onOk() {
							form.submit();
						},
						onCancel() {
							setConfirmLoading(false);
						},
						okText: 'Confirm',
					});
				});
			}}
			confirmLoading={confirmLoading}
			title={<span>Copy supplier assignments from {selectedLocation.name}</span>}
			destroyOnClose
		>
			<Form
				form={form}
				layout="vertical"
				className="supplierForm"
				initialValues={{}}
				onFinish={onSubmit}
			>
				<Alert
					type="info"
					showIcon
					className="!mb-6"
					message={
						hasProblemTypeFilter ? (
							<div>
								<div className="mb-2">
									Only the following filtered problem types will be copied:
								</div>
								<ProblemTypeHierarchicalDisplay
									problemTypeIds={problemTypeIds}
									displayMode="pill"
								/>
							</div>
						) : (
							'All problem types will be copied to the selected locations.'
						)
					}
				/>
				<div className="mb-6 flex items-start gap-3">
					<FormItem className="mb-0 min-w-[20%]" label="Copy from">
						<Select
							disabled
							className="w-full"
							value={selectedLocation.name}
							options={[{ label: selectedLocation.name, value: selectedLocation.name }]}
						/>
					</FormItem>
					<FormItem className="mb-0" label={<span>&nbsp;</span>} colon={false}>
						<ArrowRightOutlined translate="" className="text-lg text-gray-400" />
					</FormItem>
					<FormItem
						className="mb-0 min-w-[50%] flex-1"
						name="locationIds"
						label="Copy to"
						required
						rules={[
							{
								required: true,
								type: 'array',
								min: 1,
								message: 'Please select at least one location',
							},
						]}
					>
						<RegionsLocationsTreeSelect
							mode="multiple"
							noIsActiveFilter
							filtersSlice={locations}
							targetCollectionName="SUPPLIER_PRIVATE_NETWORK_COPY_TO_LOCATIONS_AUTOCOMPLETE"
							hiddenRecords={[selectedLocation.id]}
						/>
					</FormItem>
				</div>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	locations: state.locations,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	copyRankings: ({ fromLocationId, toLocationIds, problemTypeIds }) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? copyRankForSuppliersToLocationsForSupplier({
						fromLocationId,
						toLocationIds,
						problemTypeIds,
				  })
				: copyRankForSuppliersToLocationsForBuyer({ fromLocationId, toLocationIds, problemTypeIds })
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(CopyRankingToLocationsComponent)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
